// Chord charts for export (F8.1–F8.4): the song as bars of chord names, in the player's
// notation, transpose and capo.
import { barOfBeat, beatInBar } from '../analysis/grid';
import type { AnalysisResult } from '../analysis/types';
import { chordName, NO_CHORD_LABEL, soundingKeyName, type DisplayOptions } from '../theory/display';
import { noteName, keyUsesFlats } from '../theory/key';
import { mod } from '../util/math';

/** One bar: the chord names that start in it, in order ("—" for none, e.g. a held chord). */
export interface ChartBar {
  number: number;
  chords: string[];
}

export interface SongInfo {
  title: string;
  artist?: string;
}

/**
 * The song as bars. A bar lists each chord that starts in it; a bar where the previous chord
 * simply continues lists that chord again, as players expect on a chart.
 */
export function chartBars(result: AnalysisResult, o: DisplayOptions): ChartBar[] {
  const grid = result.beatGrid;
  const bars = new Map<number, string[]>();
  let last: string | null = null;
  for (const seg of result.segments) {
    const name = chordName(seg.chord, o);
    for (let b = seg.startBeat; b < seg.startBeat + seg.beatCount; b++) {
      const bar = Math.max(0, barOfBeat(grid, b));
      const list = bars.get(bar) ?? [];
      const first = b === seg.startBeat || beatInBar(grid, b) === 1;
      if (
        first &&
        list.at(-1) !== name &&
        !(list.length === 0 && seg.chord === null && last === null)
      ) {
        list.push(name);
      }
      bars.set(bar, list);
      last = name;
    }
  }
  const list = [...bars.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([number, chords]) => ({ number, chords }));
  // A pickup or silent intro with no chord at all isn't worth a bar on the chart.
  const first = list.findIndex((b) => b.chords.some((name) => name !== NO_CHORD_LABEL));
  return first > 0 ? list.slice(first) : list;
}

/** "Key G major · 100 BPM · 4/4 · Capo 2 (E shapes)". */
export function chartSummary(result: AnalysisResult, o: DisplayOptions): string {
  const parts = [
    `Key ${soundingKeyName(o)}`,
    `${Math.round(result.beatGrid.bpm)} BPM`,
    `${result.beatGrid.beatsPerBar}/4`,
  ];
  if (o.capo > 0) {
    const shape = mod(o.key.tonic + o.transpose - o.capo, 12);
    const shapeKey =
      noteName(shape, keyUsesFlats(shape, o.key.mode)) + (o.key.mode === 'minor' ? 'm' : '');
    parts.push(`Capo ${o.capo} (${shapeKey} shapes)`);
  }
  return parts.join(' · ');
}

/** Plain-text chord chart (F8.4): bars in fixed-width columns, four to a line. */
export function chartText(
  song: SongInfo,
  result: AnalysisResult,
  o: DisplayOptions,
  barsPerLine = 4,
): string {
  const bars = chartBars(result, o);
  const cells = bars.map((b) => (b.chords.length ? b.chords.join(' ') : '%'));
  const width = Math.max(6, ...cells.map((c) => c.length)) + 2;
  const lines: string[] = [];
  for (let i = 0; i < cells.length; i += barsPerLine) {
    const row = cells.slice(i, i + barsPerLine).map((c) => ` ${c.padEnd(width - 1)}`);
    lines.push(`|${row.join('|')}|`);
  }
  const head = song.artist ? `${song.title} — ${song.artist}` : song.title;
  return [head, chartSummary(result, o), '', ...lines, '', 'Chords: KnowYourChord'].join('\n');
}

/**
 * ChordPro (F8.3), the standard chord-sheet format. Chord names are always letters, which every
 * ChordPro reader understands; bars are separated by "|".
 */
export function chartChordPro(song: SongInfo, result: AnalysisResult, o: DisplayOptions): string {
  const letters: DisplayOptions = { ...o, notation: 'letters' };
  const bars = chartBars(result, letters);
  const directives = [
    `{title: ${song.title}}`,
    ...(song.artist ? [`{artist: ${song.artist}}`] : []),
    `{key: ${soundingKeyName(o).replace(' major', '').replace(' minor', 'm')}}`,
    `{tempo: ${Math.round(result.beatGrid.bpm)}}`,
    `{time: ${result.beatGrid.beatsPerBar}/4}`,
    ...(o.capo > 0 ? [`{capo: ${o.capo}}`] : []),
  ];
  const lines: string[] = [];
  for (let i = 0; i < bars.length; i += 4) {
    const row = bars
      .slice(i, i + 4)
      .map((b) => (b.chords.length ? b.chords.map((c) => `[${c}]`).join(' ') : '%'));
    lines.push(`| ${row.join(' | ')} |`);
  }
  return [...directives, '', ...lines, ''].join('\n');
}
